import { Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppDataService } from 'src/app/shared/services/app-data.service';
import { AccessGroup, ChargingUser, ChargingUserAccessGroup } from '../charging-user.model';
import { Observable, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

declare const google: any;

@Component({
  selector: 'adm-app-emulator-for-access',
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatMenuModule, MatTooltipModule],
  templateUrl: './app-emulator-for-access.component.html',
  styleUrl: './app-emulator-for-access.component.scss',
})
export class AppEmulatorForAccessComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<AppEmulatorForAccessComponent>>(MatDialogRef);
  private appDataService = inject(AppDataService);
  data = inject<{
    chargingUser: ChargingUser;
  }>(MAT_DIALOG_DATA);

  public chargingUser: ChargingUser;
  public accessGroups$: Observable<ChargingUserAccessGroup>;
  public accessGroups: AccessGroup[] = [];
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.chargingUser = this.data.chargingUser;
    if (!this.chargingUser.deviceId && !this.chargingUser.mokUserNumber) {
      return;
    }

    this.accessGroups$ = this.appDataService.getAccessGroups(this.chargingUser.mokUserNumber ?? 0, this.chargingUser.deviceId ?? '');

    this.accessGroups$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.accessGroups = result.accessGroups.filter(group => group.locations.length > 0);
      setTimeout(() => this.initMaps(), 0); // Ensure DOM is ready
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Trigger completion of observables
    this.destroy$.complete(); // Clean up the destroy subject
  }

  async initMaps() {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    this.accessGroups.forEach((group, groupIndex) => {
      if (!group.locations.length) return;

      // Group-level map (big map)
      const groupMapElement = document.getElementById(`map-${groupIndex}`);

      if (groupMapElement) {
        const groupMap = new Map(groupMapElement, {
          zoom: 7,
          center: {
            lat: group.locations[0].latitude,
            lng: group.locations[0].longitude,
          },
          mapId: environment.googleMapId,
          disableDefaultUI: true,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          clickableIcons: false,
        });

        group.locations.forEach(location => {
          const { markerDiv, url } = this.createCustomMarker(group, location);
          const marker = new AdvancedMarkerElement({
            map: groupMap,
            position: {
              lat: location.latitude,
              lng: location.longitude,
            },
            content: markerDiv,
            gmpClickable: true,
          });
          marker.addEventListener('gmp-click', () => window.open(url, '_blank'));
        });
      }
    });
  }

  async onMenuOpened(groupIndex: number) {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const group = this.accessGroups[groupIndex];
    if (!group) return;

    group.locations.forEach((location, locIndex) => {
      const miniMapId = `map-${groupIndex}-${locIndex}`;

      const miniMapEl = document.getElementById(miniMapId);

      if (!miniMapEl) return;

      const miniMap = new Map(miniMapEl, {
        zoom: 16,
        center: {
          lat: location.latitude,
          lng: location.longitude,
        },
        mapId: environment.googleMapId,
        disableDefaultUI: true,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        clickableIcons: false,
      });

      const { markerDiv: miniMpaMarkerDiv, url: miniMapUrl } = this.createCustomMiniMapMarker(location);

      const miniMarker = new AdvancedMarkerElement({
        map: miniMap,
        position: {
          lat: location.latitude,
          lng: location.longitude,
        },
        content: miniMpaMarkerDiv,
        gmpClickable: true,
      });
      miniMarker.addEventListener('gmp-click', () => window.open(miniMapUrl, '_blank'));
    });
  }

  private createCustomMarker(group: any, location: any): { markerDiv: HTMLElement; url: string } {
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'relative';
    markerDiv.style.width = '40px';
    markerDiv.style.height = '40px';
    markerDiv.style.cursor = 'pointer';

    let locationUrl = `/ok-app-emulator?mapSearchInputType=location&query=${location.locationId}&accessGroupIdentifier=${group.accessGroupIdentifier}`;
    if (this.chargingUser.deviceId) {
      locationUrl = `/ok-app-emulator?mapSearchInputType=deviceId&query=${this.chargingUser.deviceId}&accessGroupIdentifier=${group.accessGroupIdentifier}`;
    }
    if (this.chargingUser.mokUserNumber) {
      locationUrl = `/ok-app-emulator?mapSearchInputType=mokUserNumber&query=${this.chargingUser.mokUserNumber}&accessGroupIdentifier=${group.accessGroupIdentifier}`;
    }

    const logoUrl = group.compactOrganizationLogoLink || '/assets/icons/map-pin.svg';
    markerDiv.innerHTML = `
      <img src="${logoUrl}" style="width: 100%; height: 100%; background-color: white; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 5px #000;">
    `;

    return { markerDiv, url: locationUrl };
  }

  private createCustomMiniMapMarker(location: any): { markerDiv: HTMLElement; url: string } {
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'relative';
    markerDiv.style.width = '20px';
    markerDiv.style.height = '20px';
    markerDiv.style.cursor = 'pointer';

    const locationUrl = `/ok-app-emulator?mapSearchInputType=location&query=${location.locationId}`;

    const logoUrl = '/assets/img/emulator-app-marker-icon.svg';
    markerDiv.innerHTML = `
      <img src="${logoUrl}">
    `;

    return { markerDiv, url: locationUrl };
  }
}
